import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchLiveStreamers } from "../lib/api";
import { AvatarChip } from "./AvatarChip";
import { useAuth } from "../context/AuthContext";

const PLATFORM_ICON = {
  twitch: "fab fa-twitch",
  youtube: "fab fa-youtube",
  kick: "fas fa-tower-broadcast",
};

/**
 * Landing-page ticker showing currently-live PRO operators.
 * Auto-refreshes every 60s. Renders nothing when 0 are live so the hero
 * stays clean.
 */
export function LiveStreamersTicker() {
  const { user } = useAuth();
  const [data, setData] = useState({ count: 0, streamers: [] });

  useEffect(() => {
    let alive = true;
    const load = () => fetchLiveStreamers(12).then(
      (d) => alive && setData(d || { count: 0, streamers: [] }),
    ).catch(() => {});
    load();
    const iv = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  // Free signed-in users see a contextual upgrade CTA at the end of the
  // strip. Anonymous visitors don't (the sign-in funnel comes first).
  const isPaid = user && (
    user.user_type === "pro"
    || ["pro", "elite"].includes((user.plan || "").toLowerCase())
  );
  const showUpsell = !!user && !isPaid;

  if (!data.count && !showUpsell) return null;

  return (
    <div
      data-testid="live-streamers-ticker"
      className="mt-6 mb-2 rounded-2xl border border-red-500/30 bg-gradient-to-r from-red-500/10 via-black/30 to-red-500/5 backdrop-blur-sm p-3 sm:p-4"
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <p className="text-[11px] tracking-[3px] text-red-400 font-bold flex items-center gap-2">
          <span className="relative inline-flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
          </span>
          <span data-testid="live-streamers-count">
            {data.count
              ? `${data.count} PRO ${data.count === 1 ? "OPERATOR" : "OPERATORS"} LIVE`
              : "BE THE FIRST TO GO LIVE"}
          </span>
        </p>
        <p className="text-[10px] text-white/40 tracking-widest">
          UPDATES EVERY 60S
        </p>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1 no-scrollbar">
        {data.streamers.map((s) => {
          const plat = (s.live.platform || "").toLowerCase();
          const icon = PLATFORM_ICON[plat] || "fas fa-broadcast-tower";
          return (
            <Link
              key={s.username}
              to={`/u/${s.username}`}
              data-testid={`live-streamer-${s.username}`}
              className="group flex-shrink-0 w-44 rounded-xl border border-red-500/30 bg-black/40 hover:bg-red-500/10 hover:border-red-500/60 transition p-2"
              title={s.live.title || "Live now"}
            >
              <div className="flex items-center gap-2">
                <AvatarChip avatarId={s.avatar_id} pfpUrl={s.pfp_url} size={34} />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-white truncate group-hover:text-red-300 transition">
                    {s.name}
                  </p>
                  <p className="text-[10px] text-[#14b8a6]/80 truncate">
                    <i className={`${icon} mr-1`} />
                    {plat ? plat.toUpperCase() : "LIVE"}
                    {Number.isFinite(s.live.viewer_count) && s.live.viewer_count > 0 && (
                      <span className="text-white/50 ml-1.5">
                        · {s.live.viewer_count.toLocaleString()}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              {s.live.title && (
                <p className="mt-1.5 text-[10px] text-white/60 line-clamp-2 leading-snug">
                  {s.live.title}
                </p>
              )}
            </Link>
          );
        })}
        {showUpsell && (
          <Link
            to="/pricing"
            data-testid="live-streamers-upsell-cta"
            className="group flex-shrink-0 w-44 rounded-xl border-2 border-dashed border-[#14b8a6]/40 bg-[#14b8a6]/5 hover:bg-[#14b8a6]/15 hover:border-[#14b8a6]/80 transition p-3 flex flex-col items-start justify-between"
            title="Upgrade to PRO to feature your stream on the landing page"
          >
            <i className="fas fa-bolt text-[#14b8a6] text-base" />
            <p className="mt-1 text-xs font-bold text-[#14b8a6] tracking-wider">
              GO LIVE HERE
            </p>
            <p className="text-[10px] text-white/60 leading-snug mt-0.5">
              Upgrade to PRO &amp; get featured on the landing page when you stream.
            </p>
          </Link>
        )}
      </div>
    </div>
  );
}

export default LiveStreamersTicker;
